import React from 'react';
import { TaskType } from '@/shared/domain/taskTypes';
/**
 * Map a task type to the noun used in the "without any X" prompt.
 *
 * @param {string} taskType
 * @returns {string}
 */
const labelForTaskType = (taskType) => {
    if (taskType === TaskType.classification) return 'labels';
    if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
        return 'entities';
    }
    if (taskType === TaskType.summarization) return 'summaries';
    if (taskType === TaskType.contentGeneration) return 'responses';
    return 'outputs';
};

/**
 * Build the confirmation message for empty output columns.
 *
 * Single-objective (`usecasesWithMissingOutputs === null`): one task-type-specific
 * prompt (e.g. "labels", "entities", "summaries").
 *
 * Multi-objective: one sub-task names that sub-task and its task type; multiple
 * sub-tasks list their names with generic "outputs" wording.
 *
 * @param {string} taskType - Active task type (single-objective path only).
 * @param {null|Array<{ usecase_name: string, task_type: string }>} usecasesWithMissingOutputs
 *   `null` in single-objective mode; otherwise sub-tasks with missing/empty outputs.
 * @returns {string}
 */
function errorMessage(taskType, usecasesWithMissingOutputs) {
    if (usecasesWithMissingOutputs === null) {
        const noun = labelForTaskType(taskType);
        return `Are you sure you want to upload a file without any ${noun}?`;
    }

    if (noOutputsErrors.length == 1) {
        const output = noOutputsErrors[0];
        const noun = labelForTaskType(output.task_type);
        return `Are you sure you want to upload a file without any ${noun} for sub-task "${output.usecase_name}"?`;
    }

    const taskNames = noOutputsErrors.map(error => error.usecase_name);
    const taskNamesString = taskNames.join('", "');
    return `Are you sure you want to upload a file without any of the outputs? Sub-tasks without outputs: "${taskNamesString}"`;
}

/**
 * Step body shown when the user clicks Done with one or more empty output
 * columns. The modal owns the footer + step transitions; this component
 * only renders the question panel.
 *
 * Message is task-type-specific in single-objective mode. In multi-objective mode
 * the parent passes the sub-tasks that lack outputs so the message can name
 * each affected sub-task (or list several when more than one is empty).
 *
 * @param {Object} props
 * @param {string} [props.taskType] - Task type for the single-objective path
 *   (`usecasesWithMissingOutputs === null`).
 * @param {null|Array<{ id: string, usecase_name: string, task_type: string }>} [props.usecasesWithMissingOutputs]
 *   `null` when not in multi-objective mode; otherwise sub-tasks with missing outputs
 *   (from `DatasetUploadStepsModal` validation state).
 */
const ConfirmNoOutputsStep = ({ taskType, usecasesWithMissingOutputs }) => {
    return (
        <div style={{ marginBottom: '24px' }}>
            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '16px',
                }}
            >
                Confirm Upload
            </div>
            <div
                style={{
                    fontSize: '14px',
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#f6f8fa',
                    borderRadius: '8px',
                    border: '1px solid #e1e4e8',
                    color: 'red',
                }}
            >
                <div style={{ marginBottom: '8px' }}>
                    <strong>{errorMessage(taskType, usecasesWithMissingOutputs)}</strong>
                </div>
            </div>
        </div>
    );
};

export default ConfirmNoOutputsStep;
